import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase/config';
import {
    collection,
    query,
    where,
    orderBy,
    onSnapshot,
    limit,
    updateDoc,
    doc,
    writeBatch
} from 'firebase/firestore';
import { Bell, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

export default function NotificationBell() {
    const { currentUser } = useAuth();
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    // Close dropdown if clicked outside
    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [dropdownRef]);

    // Listen to notifications
    useEffect(() => {
        if (!currentUser) return;

        const q = query(
            collection(db, 'notifications'),
            where('userId', '==', currentUser.uid),
            orderBy('createdAt', 'desc'),
            limit(10)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const notes = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setNotifications(notes);
            setUnreadCount(notes.filter(n => !n.read).length);
        });

        return unsubscribe;
    }, [currentUser]);

    const markAllRead = async () => {
        const batch = writeBatch(db);
        notifications.forEach(note => {
            if (!note.read) {
                const ref = doc(db, 'notifications', note.id);
                batch.update(ref, { read: true });
            }
        });
        await batch.commit();
    };

    const handleBellClick = () => {
        setIsOpen(!isOpen);
        if (!isOpen && unreadCount > 0) {
            markAllRead(); // Optional: Mark read immediately on open, or let user click individual
        }
    };

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={handleBellClick}
                className="relative p-2 text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
            >
                <Bell className="w-6 h-6" />
                {unreadCount > 0 && (
                    <span className="absolute top-1 right-1 flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-500 rounded-full border-2 border-white">
                        {unreadCount}
                    </span>
                )}
            </button>

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden z-50"
                    >
                        <div className="p-4 border-b border-gray-100 flex justify-between items-center">
                            <h3 className="font-semibold text-gray-900">Notifications</h3>
                            {unreadCount > 0 && (
                                <button onClick={markAllRead} className="text-xs text-blue-600 hover:text-blue-700">
                                    Mark all read
                                </button>
                            )}
                        </div>

                        <div className="max-h-96 overflow-y-auto">
                            {notifications.length > 0 ? (
                                notifications.map((note) => (
                                    <Link
                                        key={note.id}
                                        to={note.link || '#'}
                                        onClick={() => setIsOpen(false)}
                                        className={`block p-4 border-b border-gray-50 hover:bg-gray-50 transition-colors ${!note.read ? 'bg-blue-50/50' : ''}`}
                                    >
                                        <div className="flex items-start">
                                            <div className="flex-1">
                                                <p className={`text-sm ${!note.read ? 'font-semibold text-gray-900' : 'text-gray-600'}`}>
                                                    {note.title}
                                                </p>
                                                <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                                                    {note.message}
                                                </p>
                                                <p className="text-xs text-gray-400 mt-2">
                                                    {note.createdAt?.seconds ? new Date(note.createdAt.seconds * 1000).toLocaleDateString() : 'Just now'}
                                                </p>
                                            </div>
                                            {!note.read && (
                                                <div className="w-2 h-2 bg-blue-600 rounded-full mt-2" />
                                            )}
                                        </div>
                                    </Link>
                                ))
                            ) : (
                                <div className="p-8 text-center text-gray-500">
                                    <Bell className="w-8 h-8 mx-auto mb-2 opacity-20" />
                                    <p className="text-sm">No notifications yet</p>
                                </div>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}